import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Trophy, Medal, Award, TrendingUp } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Leaderboards = () => {
  const [totalPointsLeaderboard, setTotalPointsLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTotalPointsLeaderboard();
  }, []);

  const fetchTotalPointsLeaderboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/leaderboards/total-points`, {
        withCredentials: true
      });
      setTotalPointsLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error('Failed to fetch leaderboards:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCarrotIcon = (rank) => {
    const carrotMap = {
      1: '/carrot_gold.png',
      2: '/carrot_silver.png',
      3: '/carrot_bronze.png',
      4: '/carrot_orange.png',
      5: '/carrot_orange.png'
    };
    return carrotMap[rank] || null;
  };

  const getRankIcon = (rank) => {
    const carrotIcon = getCarrotIcon(rank);
    if (carrotIcon) {
      return <img src={carrotIcon} alt={`Rank ${rank}`} className="w-8 h-8" />;
    }
    return <span className="w-8 h-8 flex items-center justify-center text-xs font-bold text-gray-500 bg-gray-100 rounded-full">#{rank}</span>;
  };

  const getRankBgColor = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-400';
    if (rank === 2) return 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-400';
    if (rank === 3) return 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-400';
    if (rank === 4 || rank === 5) return 'bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-300';
    return 'bg-white border-gray-200';
  };

  if (loading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Leaderboards
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 text-center py-4">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          Total Points Leaderboard
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
          {totalPointsLeaderboard.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No scores yet. Be the first!</p>
          ) : (
            totalPointsLeaderboard.map((entry) => (
              <div
                key={entry.profile_id}
                className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all hover:shadow-md ${getRankBgColor(entry.rank)}`}
              >
                {/* Rank Icon & Number */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {getRankIcon(entry.rank)}
                  {entry.rank <= 5 && (
                    <span className="text-xs font-bold text-gray-700">
                      #{entry.rank}
                    </span>
                  )}
                </div>

                {/* Avatar */}
                <img
                  src={entry.avatar}
                  alt={entry.profile_name}
                  className="w-10 h-10 rounded-full border-2 border-white shadow-sm"
                  onError={(e) => {
                    e.target.src = '/logo.png';
                  }}
                />

                {/* Profile Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">
                    {entry.profile_name}
                  </p>
                  <p className="text-xs text-gray-600">
                    🔥 {entry.streak} day{entry.streak !== 1 ? 's' : ''} streak
                  </p>
                </div>

                {/* Points */}
                <div className="flex-shrink-0 text-right">
                  <p className="font-bold text-lg text-blue-600">
                    {entry.total_points.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">points</p>
                </div>
              </div>
            ))
          )}
        </div>

        {totalPointsLeaderboard.length > 10 && (
          <p className="text-xs text-gray-500 text-center mt-3">
            Scroll to see more profiles
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default Leaderboards;
